'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import Step1Account from './Step1Account';
import Step2Store from './Step2Store';
import Step3Business from './Step3Business';
import { supabase } from '@/lib/supabase';

export interface SignUpFormData {
  // Step 1
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  // Step 2
  storeName: string;
  businessCategory: string;
  storeDescription: string;
  sellerType: 'hyperlocal' | 'national' | 'both';
  // Step 3
  businessName: string;
  businessType: string;
  panNumber: string;
  gstNumber: string;
  businessAddress: string;
  city: string;
  state: string;
  pincode: string;
  bankAccountNumber: string;
  bankIfsc: string;
  bankName: string;
}

const STEPS = [
  { id: 'step-account', label: 'Account', description: 'Your login details' },
  { id: 'step-store', label: 'Store', description: 'Store information' },
  { id: 'step-business', label: 'Business', description: 'Business & banking' },
];

export default function SellerSignUpForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const router = useRouter();

  const form = useForm<SignUpFormData>({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      storeName: '',
      businessCategory: '',
      storeDescription: '',
      sellerType: 'national',
      businessName: '',
      businessType: '',
      panNumber: '',
      gstNumber: '',
      businessAddress: '',
      city: '',
      state: '',
      pincode: '',
      bankAccountNumber: '',
      bankIfsc: '',
      bankName: '',
    },
    mode: 'onBlur',
  });

  const progressPercent = ((currentStep + 1) / STEPS.length) * 100;

  const handleNext = async () => {
    let fieldsToValidate: (keyof SignUpFormData)[] = [];
    if (currentStep === 0) {
      fieldsToValidate = ['fullName', 'email', 'phone', 'password', 'confirmPassword'];
    } else if (currentStep === 1) {
      fieldsToValidate = ['storeName', 'businessCategory', 'storeDescription', 'sellerType'];
    }

    const valid = await form.trigger(fieldsToValidate);
    if (valid) setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const onSubmit = async (data: SignUpFormData) => {
    setIsSubmitting(true);
    try {
      const siteUrl = typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_SITE_URL || 'https://bozaat5282.builtwithrocket.new');

      // Step 1: Create Supabase Auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email.trim().toLowerCase(),
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            phone: data.phone,
            role: 'seller',
          },
          emailRedirectTo: `${siteUrl}/auth/callback?next=/seller-sign-up`,
        },
      });

      if (authError) {
        toast.error(authError.message || 'Failed to create account. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Detect duplicate email (Supabase returns identities: [] silently)
      if (authData?.user && (!authData.user.identities || authData.user.identities.length === 0)) {
        toast.error('An account with this email already exists. Please sign in instead.');
        setIsSubmitting(false);
        return;
      }

      const userId = authData.user?.id;
      if (!userId) {
        toast.error('Account creation failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Step 2: Upsert user_profiles with role='seller'
      // The trigger handle_new_user_profile() creates the base row automatically.
      // We upsert to set seller-specific fields. Non-blocking on failure.
      try {
        await supabase.from('user_profiles').upsert({
          id: userId,
          email: data.email.trim().toLowerCase(),
          full_name: data.fullName,
          phone: data.phone,
          role: 'seller',
          seller_mode: data.sellerType,
          onboarding_status: 'in_progress',
          kyc_status: 'not_submitted',
          is_active: true,
          loyalty_points: 0,
          lifetime_points: 0,
        }, { onConflict: 'id' });
      } catch (profileErr) {
        console.warn('Profile upsert warning (non-fatal):', profileErr);
      }

      // Step 3: Insert into seller_onboarding
      try {
        await supabase.from('seller_onboarding').upsert({
          seller_id: userId,
          business_name: data.businessName,
          business_type: data.businessType,
          business_category: data.businessCategory,
          gst_number: data.gstNumber || null,
          pan_number: data.panNumber.toUpperCase(),
          business_address: data.businessAddress,
          city: data.city,
          state: data.state,
          pincode: data.pincode,
          bank_account_number: data.bankAccountNumber,
          bank_ifsc: data.bankIfsc.toUpperCase(),
          bank_name: data.bankName,
          store_name: data.storeName,
          store_description: data.storeDescription,
          approval_status: 'pending',
          seller_type: data.sellerType,
          delivery_radius_km: 10,
          eco_score: 85,
          ai_check_status: 'pending',
          submitted_at: new Date().toISOString(),
        }, { onConflict: 'seller_id' });
      } catch (onboardingErr) {
        console.warn('Onboarding upsert warning (non-fatal):', onboardingErr);
      }

      // Show verification email popup
      setRegisteredEmail(data.email);
      setShowVerificationPopup(true);
    } catch (err) {
      console.error('Signup error:', err);
      toast.error('Something went wrong. Please try again.');
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Email Verification Popup */}
      {showVerificationPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-card border border-border rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <Icon name="EnvelopeIcon" size={32} className="text-primary" />
            </div>

            <h2 className="text-xl font-700 text-foreground mb-2">
              Verify Your Email
            </h2>
            <p className="text-sm text-muted-foreground mb-1">
              We&apos;ve sent a confirmation link to:
            </p>
            <p className="text-sm font-600 text-primary mb-4 break-all">
              {registeredEmail}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Please check your inbox and click the confirmation link to activate your account and continue your seller registration.
            </p>

            <div className="bg-muted/50 rounded-xl p-4 mb-6 text-left space-y-2">
              <p className="text-xs font-600 text-foreground">What happens next?</p>
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 font-700 text-[10px]">1</span>
                <span>Click the link in your email to confirm your address</span>
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 font-700 text-[10px]">2</span>
                <span>You&apos;ll be redirected back to complete your registration</span>
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 font-700 text-[10px]">3</span>
                <span>Your account will be reviewed and activated by our team</span>
              </div>
            </div>

            <button
              onClick={() => router.push('/seller-login')}
              className="btn-primary w-full py-2.5 rounded-xl text-sm font-600"
            >
              Go to Login
            </button>
            <p className="text-xs text-muted-foreground mt-3">
              Didn&apos;t receive the email? Check your spam folder or{' '}
              <button
                onClick={() => setShowVerificationPopup(false)}
                className="text-primary hover:underline font-600"
              >
                try again
              </button>
            </p>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
        {/* Progress Bar */}
        <div className="px-8 pt-6 pb-0">
          {/* Step indicators */}
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((step, idx) => (
              <div
                key={step.id}
                className="flex items-center gap-2"
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-700 flex-shrink-0 transition-all duration-300 ${
                    idx < currentStep
                      ? 'bg-primary text-primary-foreground'
                      : idx === currentStep
                      ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {idx < currentStep ? (
                    <Icon name="CheckIcon" size={14} />
                  ) : (
                    idx + 1
                  )}
                </div>
                <div className="hidden sm:block">
                  <p
                    className={`text-xs font-600 ${
                      idx <= currentStep ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {step.description}
                  </p>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`hidden sm:block flex-1 h-0.5 mx-4 rounded-full transition-all duration-500 ${
                      idx < currentStep ? 'bg-primary' : 'bg-border'
                    }`}
                    style={{ width: '60px' }}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="w-full bg-muted rounded-full h-1.5 mb-0">
            <div
              className="bg-primary h-1.5 rounded-full progress-bar-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className="px-8 py-6">
            {currentStep === 0 && <Step1Account form={form} />}
            {currentStep === 1 && <Step2Store form={form} />}
            {currentStep === 2 && <Step3Business form={form} />}
          </div>

          {/* Navigation Buttons */}
          <div className="px-8 pb-8 flex items-center justify-between gap-4 border-t border-border pt-6">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-600 text-secondary-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeftIcon" size={16} />
              Back
            </button>

            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                Step {currentStep + 1} of {STEPS.length}
              </span>
            </div>

            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-600"
              >
                Next Step
                <Icon name="ChevronRightIcon" size={16} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-600 disabled:opacity-70 disabled:cursor-not-allowed min-w-[140px] justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <Icon name="CheckIcon" size={16} />
                    Create Account
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Bottom link */}
        <div className="text-center pb-6 text-sm text-muted-foreground">
          Already have an account?{' '}
          <a href="/seller-login" className="text-primary font-600 hover:underline">
            Login here
          </a>
        </div>
      </div>
    </>
  );
}